<template>
  <Form :validation-schema="validationSchema" :class="$style.form" @invalid-submit="onError" @submit="onSubmit">
    <ItemOptions v-model="itemOptionVariantSlugs" v-model:data="itemOptions" :item-id="itemId" />
    <div :class="$style.cartButtonWrap">
      <div :class="$style.cartButton">
        <IButton type="primary" native-type="submit" :disabled="!available">{{ buttonLabel }}</IButton>
        <button v-if="hasOption" type="button" :class="$style.cartButtonOption" @click="onClickSelectOptionButton">購入オプションを選ぶ</button>
      </div>
    </div>
  </Form>
</template>

<script setup lang="ts">
import type { ItemOption } from '@iichi/schema-models';
import { toTypedSchema } from '@vee-validate/zod';
import { Form } from 'vee-validate';
import { ref, computed } from 'vue';
import * as z from 'zod';
import { scrollToError } from '@/js/lib/helper/validate';
import { addToBasket } from '@/js/lib/api/v1/cart';
import { useCartStore } from '@/js/lib/store/cart';
import { useCommonStore } from '@/js/lib/store/common';
import { useUserStore } from '@/js/lib/store/user';
import { info } from '@/js/lib/ui';
import { mountModal } from '@/js/lib/ui/helper/dynamic-modal';
import IButton from '@/js/components/molecules/IButton.vue';
import AlreadyInCartModal from '@/js/components/www.iichi.com/organisms/modals/AlreadyInCartModal.vue';
import CartModal from '@/js/components/www.iichi.com/organisms/modals/CartModal.vue';
import ItemOptions from './ItemOptions.vue';

const props = defineProps<{
  itemId: number;
  shopId: number;
  stock: number;
  hasOption: 0 | 1;
  isOnVacation: 0 | 1;
  isPreSaleNotStarted: boolean;
  buttonLabel?: string;
}>();
const { isWebView } = useCommonStore();
const userStore = useUserStore();
const cartStore = useCartStore();
const cartButtonEl = document.querySelector('.cart-button') as HTMLDivElement;
const itemOptionVariantSlugs = ref<string[]>([]);
const itemOptions = ref<ItemOption[]>([]);
const validationSchema = computed(() =>
  toTypedSchema(
    z.object({
      itemOptionVariantSlugs: itemOptions.value.length > 0 ? z.tuple(itemOptions.value.map((opt) => (opt.required ? z.string().min(1) : z.string().optional())) as [z.ZodString, ...z.ZodString[]]) : z.unknown(),
    }),
  ),
);
const available = computed(() => {
  if (props.shopId === userStore.memberId || props.isOnVacation || userStore.isBlackListed || props.isPreSaleNotStarted) {
    return false;
  }
  return props.stock > 0;
});

const buttonLabel = computed(() => {
  if (props.buttonLabel) {
    return props.buttonLabel;
  }

  if (props.isOnVacation) {
    return '販売お休み中';
  }

  if (props.isPreSaleNotStarted) {
    return '販売開始前';
  }

  return 'カートに入れる';
});

const onClickSelectOptionButton = () => {
  cartButtonEl.scrollIntoView({
    behavior: 'smooth',
    block: 'center',
  });
};
const onError = () => {
  scrollToError();
};
const onSubmit = async () => {
  if (available.value) {
    // 以前のeコマースオブジェクトをクリアする
    // eslint-disable-next-line unicorn/no-null
    window.dataLayer?.push({ ecommerce: null });
    // eslint-disable-next-line unicorn/prefer-single-call
    window.dataLayer?.push({
      event: 'add_to_cart',
      ecommerce: {
        items: [
          {
            item_id: props.itemId,
            quantity: 1,
          },
        ],
      },
    });
    try {
      await addToBasket({ itemId: props.itemId, itemOptionVariantSlugs: itemOptionVariantSlugs.value });
      if (isWebView) {
        info('カートに追加されました', { fadeOut: true });
        return;
      }
      await cartStore.loadBaskets();
      mountModal({
        Component: CartModal,
      });
    } catch (error) {
      window.Sentry?.captureException(error);
      if (error?.response?.data?.errors.includes('already_in_cart')) {
        mountModal({
          Component: AlreadyInCartModal,
          props: {
            itemId: props.itemId,
            itemOptionVariantSlugs: itemOptionVariantSlugs.value,
          },
        });
        return;
      }
      if (error?.response?.data?.errors.includes('stock_missing')) {
        info('注文個数が在庫数を超えています');
        return;
      }
      info('カートに追加できませんでした');
    }
  }
};
</script>

<style module lang="scss">
.form {
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;
}

.cartButtonWrap {
  height: 52px;
}

.cartButton {
  .cartButtonOption {
    display: none;
  }

  @media (--sp) {
    :global(.fixed) & {
      background-color: var(--color-white);
      bottom: 0;
      box-shadow: 0 0 3px rgb(153 153 153 / 65%);
      height: auto;
      left: 0;
      padding: 10px;
      position: fixed;
      width: 100%;
      z-index: 1;

      .cartButtonOption {
        display: block;
        margin: 10px auto 0;
        text-decoration: underline;
      }
    }
  }
}
</style>
